import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { PrimeTemplate } from 'primeng/api';
import { Dialog } from 'primeng/dialog';
import { DomHandler } from 'primeng/dom';
import { isNullOrEmpty } from '../../../core/utils/common-functions';
import { BaseComponent } from '../base.component';

const BTN_ACTION_TYPE = {
  Confirm: 'confirm',
  Cancel: 'cancel',
  Close: 'close',
};

/**
 * Override dialog (offset) 可垂直拖拉
 * reference: https://github.com/cetincakiroglu/primeng/commit/8771dcc9e663dc65b278d9ecfa354d7e2377d4ff
 */
Dialog.prototype.onDrag = function (event) {
  if (this.dragging) {
    let containerWidth = DomHandler.getOuterWidth(this.container);
    let containerHeight = DomHandler.getOuterHeight(this.container);
    let deltaX = event.pageX - this.lastPageX;
    let deltaY = event.pageY - this.lastPageY;
    let offset = this.container.getBoundingClientRect();
    let leftPos = offset.left + deltaX;
    let topPos = offset.top + deltaY;
    let viewport = DomHandler.getViewport();

    this.container.style.position = 'fixed';

    if (this.keepInViewport) {
      if (leftPos >= this.minX && leftPos + containerWidth < viewport.width) {
        this._style.left = leftPos + 'px';
        this.lastPageX = event.pageX;
        this.container.style.left = leftPos + 'px';
      }

      if (topPos >= this.minY && topPos + containerHeight < viewport.height) {
        this._style.top = topPos + 'px';
        this.lastPageY = event.pageY;
        this.container.style.top = topPos + 'px';
      }
    } else {
      this.lastPageX = event.pageX;
      this.container.style.left = leftPos + 'px';
      this.lastPageY = event.pageY;
      this.container.style.top = topPos + 'px';
    }
  }
};

/**
 * 彈跳視窗元件
 *
 * @export
 * @class ContentDialogComponent
 * @extends {BaseComponent}
 * @implements {OnInit}
 * @implements {AfterViewInit}
 */
@Component({
  selector: 'sigv-content-dialog',
  templateUrl: './content-dialog.component.html',
  styleUrls: ['./content-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentDialogComponent
  extends BaseComponent
  implements OnInit, AfterViewInit
{
  @Input() baseZIndex = 0;

  /**
   * 是否顯示
   */
  @Input() visible = false;

  /**
   * 可拖拉
   */
  @Input() draggable = false;

  /**
   * 背景遮罩
   */
  @Input() modal = true;

  /**
   * 視窗大小
   * small: 小; medium: 中; large: 大
   */
  @Input() dialogSize: 'small' | 'medium' | 'large' = 'medium';

  /**
   * 顯示取消按鈕
   */
  @Input() showCancel = true;

  /**
   * 顯示確認按鈕
   */
  @Input() showConfirm = true;

  /**
   * 視窗標題
   */
  @Input() header: any;

  /**
   * 確認按鈕文字
   */
  @Input() confirmText?: string;

  /**
   * 取消按鈕文字
   */
  @Input() cancelText?: string;
  @Input() closable = true;

  /**
   * 異動日期:2024/04/10
   * 異動內容:
   * 是否讓右上角X觸發cancel事件，預設true
   */
  @Input() isOnlyCloseDialogWithX = true;

  /**
   * 異動日期:2025/06/17
   * 異動內容:
   * 是否取消或者關閉按鈕可以觸發cancel事件，預設false
   */
  @Input() isOnlyCloseDialogWithCancel = false;
  /**
   * 確認前動作
   */
  @Input() beforeConfirmAction!: () => boolean;

  /**
   * 變更顯示事件
   */
  @Output() visibleChange = new EventEmitter<boolean>();

  /**
   * 取消事件
   */
  @Output() cancel: EventEmitter<any> = new EventEmitter<any>();

  /**
   * 確認事件
   */
  @Output() confirm: EventEmitter<any> = new EventEmitter<any>();

  @ContentChildren(PrimeTemplate) templates!: QueryList<PrimeTemplate>;
  headerTemplate!: TemplateRef<any>;
  footerTemplate!: TemplateRef<any>;
  /** 行為: cancel / confirm */
  actionType: string | null = null;

  constructor(private injector: Injector, private cd: ChangeDetectorRef) {
    super(injector);
  }

  ngOnInit(): void {}

  get displayConfirmText(): string {
    return this.confirmText ?? 'Confirm';
  }

  get displayCancelText(): string {
    return this.cancelText ?? 'Cancel';
  }
  get useHeader(): string {
    let thisHeader = this.header;
    try {
      thisHeader = this.translateService.instant(this.header);
    } catch (error) {}
    return thisHeader;
  }

  /**
   * AfterViewInit 生命週期(實作 AfterViewInit 介面)
   *
   * @memberof DataTableComponent
   */
  ngAfterViewInit(): void {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'header':
          this.headerTemplate = item.template;
          break;
        case 'footer':
          this.footerTemplate = item.template;
          break;
        default:
          break;
      }
    });
    this.cd.detectChanges();
  }

  /**
   * 顯示視窗
   *
   * @param {*} event
   * @memberof ContentDialogComponent
   */
  showDialog(event: any): void {
    this.visible = true;
    this.actionType = null;
    this.visibleChange.emit(true);
  }

  /**
   * 隱藏視窗
   *
   * @param {*} event
   * @memberof ContentDialogComponent
   */
  hideDialog(event: any): void {
    this.visible = false;
    this.visibleChange.emit(false);

    // 若 actionType 沒有值，表示非按鈕觸發的行為，而是點擊右上角 x 關閉 dialog 的行為(等同於 cancel)
    if (
      (isNullOrEmpty(this.actionType) && this.isOnlyCloseDialogWithX) ||
      this.actionType === BTN_ACTION_TYPE.Cancel
    ) {
      const actionType = this.actionType ?? BTN_ACTION_TYPE.Close;
      this.cancel.emit({ originEvent: event, actionType });
    }
    this.actionType = null;
  }

  /**
   * 取消按鈕 Click 事件
   *
   * @param {*} event
   * @memberof ContentDialogComponent
   */
  cancelBtnClick(event: any): void {
    this.actionType = BTN_ACTION_TYPE.Cancel;
    this.visible = false;
    this.visibleChange.emit(false);
    if (this.isOnlyCloseDialogWithCancel) {
      const actionType = this.actionType ?? BTN_ACTION_TYPE.Close;
      this.cancel.emit({ originEvent: event, actionType });
    }
  }

  /**
   * 確認按鈕 Click 事件
   *
   * @param {*} event
   * @memberof ContentDialogComponent
   */
  confirmBtnClick(event: any): void {
    const continueStatus =
      this.beforeConfirmAction === undefined
        ? true
        : this.beforeConfirmAction();
    if (continueStatus) {
      this.actionType = BTN_ACTION_TYPE.Confirm;
      this.confirm.emit({
        originEvent: event,
        actionType: BTN_ACTION_TYPE.Confirm,
      });
    }
    this.actionType = null;
  }
}
